import { canAddInteractiveBlock, canAddMedia, usesLeadMedia } from './composition';
import {
  addEmptyTextBlock,
  insertContentBlock,
  MEDIA_BLOCK_TYPES,
  removeContentBlock,
  updateTextBlock,
} from './contentDocument';
import { getNextContentId } from './contentIds';

const BLOCK_COLLECTIONS = {
  post_polls: { collection: 'post_polls', idKey: 'pollId' },
  post_quizzes: { collection: 'post_quizzes', idKey: 'quizId' },
  post_pictures: { collection: 'post_pictures', idKey: 'groupId' },
  post_artworks: { collection: 'post_artworks', idKey: 'groupId' },
  post_videos: { collection: 'post_videos', idKey: 'groupId' },
};

const interactiveCount = state => (
  (state.post_polls || []).length + (state.post_quizzes || []).length
);

const mediaCount = state => (
  (state.post_pictures || []).length + (state.post_artworks || []).length
    + (state.post_videos || []).length
);

function createBlockItem(blockType, id, data) {
  switch (blockType) {
  case 'post_polls':
    return { pollId: id, ...data };
  case 'post_quizzes':
    return { quizId: id, ...data };
  case 'post_pictures':
  case 'post_artworks':
  case 'post_videos':
    return { groupId: id, ...data };
  default:
    return null;
  }
}

function createAtomicBlock(state, action, composition) {
  const definition = BLOCK_COLLECTIONS[action.blockType];
  if (!definition) return state;

  const isMedia = MEDIA_BLOCK_TYPES.includes(action.blockType);
  if (
    (isMedia && !canAddMedia(composition, mediaCount(state)))
    || (!isMedia && !canAddInteractiveBlock(composition, interactiveCount(state)))
  ) {
    return state;
  }

  const items = state[definition.collection] || [];
  const id = getNextContentId(items, definition.idKey);
  const item = createBlockItem(action.blockType, id, action.data);
  if (!item) return state;
  const block = { type: action.blockType, id };
  const isLeadMedia = isMedia && usesLeadMedia(composition);
  const structure = isLeadMedia
    ? [block, ...removeContentBlock(state.post_structure, action.blockType, id)]
    : insertContentBlock(state.post_structure, block, action.afterBlockKey);

  if (isLeadMedia) {
    return {
      ...state,
      [definition.collection]: [...items, item],
      post_structure: structure,
    };
  }

  const insertionIndex = structure.findIndex(
    current => current.type === block.type && current.id === block.id
  );
  if (structure[insertionIndex + 1]?.type === 'post_texts') {
    return {
      ...state,
      [definition.collection]: [...items, item],
      post_structure: structure,
    };
  }

  const emptyText = addEmptyTextBlock(state);
  structure.splice(insertionIndex + 1, 0, { type: 'post_texts', id: emptyText.textId });
  return {
    ...state,
    [definition.collection]: [...items, item],
    post_structure: structure,
    post_texts: emptyText.post_texts,
  };
}

function removeGroupItem(
  state,
  blockType,
  groupId,
  itemId,
  collection,
  itemKey,
  itemIdKey
) {
  const group = (state[collection] || []).find(item => item.groupId === groupId);
  if (!group) return state;

  if (!itemId || group[itemKey].length === 1) {
    return {
      ...state,
      post_structure: removeContentBlock(state.post_structure, blockType, groupId),
      [collection]: state[collection].filter(item => item.groupId !== groupId),
    };
  }

  return {
    ...state,
    [collection]: state[collection].map(item => (
      item.groupId === groupId
        ? {
          ...item,
          [itemKey]: item[itemKey].filter(value => value[itemIdKey] !== itemId),
        }
        : item
    )),
  };
}

function removePictureByUploadId(state, uploadId) {
  const group = (state.post_pictures || []).find(item => (
    item.pictures?.some(picture => picture.uploadId === uploadId)
  ));
  const picture = group?.pictures.find(item => item.uploadId === uploadId);

  return group && picture
    ? removeGroupItem(
      state,
      'post_pictures',
      group.groupId,
      picture.pictureId,
      'post_pictures',
      'pictures',
      'pictureId'
    )
    : state;
}

export function reduceDocument(state, action, composition) {
  switch (action.type) {
  case 'BLOCK_CREATED':
    return createAtomicBlock(state, action, composition);
  case 'BLOCK_REMOVED': {
    const definition = BLOCK_COLLECTIONS[action.blockType];
    if (!definition) return state;
    return {
      ...state,
      post_structure: removeContentBlock(state.post_structure, action.blockType, action.id),
      [definition.collection]: (state[definition.collection] || []).filter(
        item => item[definition.idKey] !== action.id
      ),
    };
  }
  case 'PICTURE_REMOVED':
    return removeGroupItem(
      state,
      'post_pictures',
      action.groupId,
      action.pictureId,
      'post_pictures',
      'pictures',
      'pictureId'
    );
  case 'PICTURE_UPLOAD_COMPLETED':
    return {
      ...state,
      post_pictures: (state.post_pictures || []).map(group => ({
        ...group,
        pictures: group.pictures.map(picture => {
          if (picture.uploadId !== action.uploadId) return picture;

          const completedPicture = { ...picture };
          delete completedPicture.uploadId;
          delete completedPicture.status;
          return { ...completedPicture, ...action.picture };
        }),
      })),
    };
  case 'PICTURE_UPLOAD_FAILED':
    return removePictureByUploadId(state, action.uploadId);
  case 'ARTWORK_REMOVED':
    return removeGroupItem(
      state,
      'post_artworks',
      action.groupId,
      action.artworkId,
      'post_artworks',
      'artworks',
      'artworkId'
    );
  case 'VIDEO_REMOVED': {
    const definition = BLOCK_COLLECTIONS.post_videos;
    return {
      ...state,
      post_structure: removeContentBlock(state.post_structure, 'post_videos', action.groupId),
      [definition.collection]: (state[definition.collection] || []).filter(
        video => video.groupId !== action.groupId
      ),
    };
  }
  case 'VIDEO_UPDATED':
    return {
      ...state,
      post_videos: (state.post_videos || []).map(video => (
        video.groupId === action.groupId
          ? { ...video, ...action.changes }
          : video
      )),
    };
  case 'VIDEO_UPDATED_BY_ASSET':
    return {
      ...state,
      post_videos: (state.post_videos || []).map(video => (
        video.assetId === action.assetId
          ? { ...video, ...action.changes }
          : video
      )),
    };
  case 'VIDEO_REMOVED_BY_ASSET': {
    const video = (state.post_videos || []).find(item => item.assetId === action.assetId);
    return video
      ? reduceDocument(state, { type: 'VIDEO_REMOVED', groupId: video.groupId }, composition)
      : state;
  }
  case 'PICTURE_CAPTION_UPDATED':
    return {
      ...state,
      post_pictures: (state.post_pictures || []).map(group => (
        group.groupId === action.groupId
          ? {
            ...group,
            pictures: group.pictures.map(picture => (
              picture.pictureId === action.pictureId
                ? { ...picture, picture_description: action.caption }
                : picture
            )),
          }
          : group
      )),
    };
  case 'TAG_ADDED': {
    const tags = state.post_tags || [];
    if (tags.some(tag => tag._id === action.tag._id)) return state;
    return { ...state, post_tags: [...tags, action.tag] };
  }
  case 'TAG_REMOVED':
    return {
      ...state,
      post_tags: (state.post_tags || []).filter(tag => tag._id !== action.tagId),
    };
  case 'POLL_UPDATED':
    return {
      ...state,
      post_polls: (state.post_polls || []).map(poll => (
        poll.pollId === action.id ? { ...poll, ...action.changes } : poll
      )),
    };
  case 'QUIZ_UPDATED':
    return {
      ...state,
      post_quizzes: (state.post_quizzes || []).map(quiz => (
        quiz.quizId === action.id ? { ...quiz, ...action.changes } : quiz
      )),
    };
  case 'TEXT_COMMITTED': {
    const postTexts = updateTextBlock(state.post_texts || [], action.id, action.content);
    if (postTexts === state.post_texts) return state;

    return {
      ...state,
      post_texts: postTexts,
    };
  }
  default:
    return state;
  }
}
